import React, {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useReducer,
  useState,
} from "react";
import {
  Challenge,
  ChallengeResults,
  ChallengeType,
  Score,
  TeamReference,
} from "@/models/pdfData";
import { v4 as uuidv4 } from "uuid";
import { scoreEquals } from "./utils";

export interface SelectedChallengeValues {
  challenge: ChallengeInProgress;
  setScoreRallyStart: (scoreRallyStart: Score) => void;
  setScoreRallyEnd: (scoreRallyEnd: Score) => void;
  teamInterruptedGame: boolean;
  setTeamInterruptedGame: Dispatch<SetStateAction<boolean>>;
  setScoreAfterChallenge: (scoreAfterChallenge: Score) => void;
  setSet: (set: number) => void;
  setTypeOfChallenge: (typeOfChallenge: ChallengeType) => void;
  setTeam: (team: TeamReference) => void;
  newChallenge: () => void;
  setChallengeResult: (challengeResult: ChallengeResults) => void;
  loadChallenge: (challenge: Challenge) => void;
}

const notInitializedYet = (): any => {
  console.error("GameDataContext has not been initialized yet!");
};
const SelectedChallengeContext = createContext<SelectedChallengeValues>({
  challenge: { id: uuidv4() },
  setScoreRallyStart: notInitializedYet,
  setScoreRallyEnd: notInitializedYet,
  teamInterruptedGame: false,
  setTeamInterruptedGame: notInitializedYet,
  setScoreAfterChallenge: notInitializedYet,
  setSet: notInitializedYet,
  setTypeOfChallenge: notInitializedYet,
  setTeam: notInitializedYet,
  setChallengeResult: notInitializedYet,
  newChallenge: notInitializedYet,
  loadChallenge: notInitializedYet,
});

interface ChallengeInProgress extends Partial<Omit<Challenge, "id">> {
  id: string;
}

type ReducerActions =
  | { type: "newChallenge" }
  | { type: "loadChallenge"; challenge: Challenge }
  | {
      type: "setScoreRallyStart";
      scoreRallyStart: Challenge["scoreRallyStart"];
    }
  | { type: "setScoreRallyEnd"; scoreRallyEnd: Challenge["scoreRallyEnd"] }
  | {
      type: "setScoreAfterChallenge";
      scoreAfterChallenge: Challenge["scoreAfterChallenge"];
    }
  | { type: "setSet"; set: Challenge["set"] }
  | {
      type: "setTypeOfChallenge";
      typeOfChallenge: Challenge["typeOfChallenge"];
    }
  | { type: "setTeam"; team: Challenge["team"] }
  | {
      type: "setChallengeResult";
      challengeResult: Challenge["challengeResult"];
    };

const reducer: React.Reducer<ChallengeInProgress, ReducerActions> = (
  state,
  action,
) => {
  switch (action.type) {
    case "newChallenge":
      return { id: uuidv4() };
    case "loadChallenge":
      if (!action.challenge.id) {
        action.challenge.id = uuidv4();
      }
      return action.challenge;
    case "setScoreRallyStart":
      return { ...state, scoreRallyStart: action.scoreRallyStart };
    case "setScoreRallyEnd":
      return { ...state, scoreRallyEnd: action.scoreRallyEnd };
    case "setScoreAfterChallenge":
      return { ...state, scoreAfterChallenge: action.scoreAfterChallenge };
    case "setSet":
      return { ...state, set: action.set };
    case "setTypeOfChallenge":
      return { ...state, typeOfChallenge: action.typeOfChallenge };
    case "setTeam":
      return { ...state, team: action.team };
    case "setChallengeResult":
      return { ...state, challengeResult: action.challengeResult };
    default:
      return state;
  }
};

export const useSelectedChallengeContext = (): SelectedChallengeValues =>
  useContext(SelectedChallengeContext);

const SelectedChallengeContextProvider: FC<PropsWithChildren<{}>> = (props) => {
  const [isInterruptingChallenge, setIsInterruptingChallenge] = useState(false);
  const [challenge, dispatch] = useReducer(reducer, { id: uuidv4() });

  const loadChallenge = (challenge: Challenge) => {
    setIsInterruptingChallenge(
      challenge.scoreRallyStart && !challenge.scoreRallyEnd,
    );
    dispatch({ type: "loadChallenge", challenge });
  };

  const setScoreRallyStart: SelectedChallengeValues["setScoreRallyStart"] = (
    scoreRallyStart,
  ) => {
    if (!scoreEquals(challenge.scoreRallyStart, scoreRallyStart)) {
      dispatch({ type: "setScoreRallyStart", scoreRallyStart });
    }
  };

  const setScoreRallyEnd: SelectedChallengeValues["setScoreRallyEnd"] = (
    scoreRallyEnd,
  ) => {
    if (!scoreEquals(challenge.scoreRallyEnd, scoreRallyEnd)) {
      dispatch({ type: "setScoreRallyEnd", scoreRallyEnd });
    }
  };

  const setScoreAfterChallenge: SelectedChallengeValues["setScoreAfterChallenge"] =
    (scoreAfterChallenge) => {
      if (!scoreEquals(challenge.scoreAfterChallenge, scoreAfterChallenge)) {
        dispatch({ type: "setScoreAfterChallenge", scoreAfterChallenge });
      }
    };

  const setSet: SelectedChallengeValues["setSet"] = (set) => {
    if (challenge.set !== set) {
      dispatch({ type: "setSet", set });
    }
  };

  const setTypeOfChallenge: SelectedChallengeValues["setTypeOfChallenge"] = (
    typeOfChallenge,
  ) => {
    if (challenge.typeOfChallenge !== typeOfChallenge) {
      dispatch({ type: "setTypeOfChallenge", typeOfChallenge });
    }
  };

  const setTeam: SelectedChallengeValues["setTeam"] = (team) => {
    if (challenge.team !== team) {
      dispatch({ type: "setTeam", team });
    }
  };

  const setChallengeResult: SelectedChallengeValues["setChallengeResult"] = (
    challengeResult,
  ) => {
    if (challenge.challengeResult !== challengeResult) {
      dispatch({ type: "setChallengeResult", challengeResult });
    }
  };

  const newChallenge: SelectedChallengeValues["newChallenge"] = () =>
    dispatch({ type: "newChallenge" });

  return (
    <SelectedChallengeContext.Provider
      value={{
        challenge,
        setScoreRallyStart,
        setScoreRallyEnd,
        teamInterruptedGame: isInterruptingChallenge,
        setTeamInterruptedGame: setIsInterruptingChallenge,
        setScoreAfterChallenge,
        setSet,
        setTypeOfChallenge,
        setTeam,
        setChallengeResult,
        loadChallenge,
        newChallenge,
      }}
    >
      {props.children}
    </SelectedChallengeContext.Provider>
  );
};

export default SelectedChallengeContextProvider;
